import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, ShieldAlert, User as UserIcon, ChefHat } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/theme";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";

const AccountRow = ({ u, onDelete }) => {
  const t = getTheme(u.business_id);
  return (
    <div
      data-testid={`account-row-${u.id}`}
      className="flex items-center justify-between gap-3 rounded-xl p-4"
      style={{ background: "var(--surface)", border: "1px solid var(--border)" }}
    >
      <div className="min-w-0 flex items-center gap-3">
        <div className="h-9 w-9 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--surface-2)", color: "var(--brand)" }}>
          {u.role === "baker" ? <ChefHat size={18} /> : <UserIcon size={18} />}
        </div>
        <div className="min-w-0">
          <div className="font-semibold truncate flex items-center gap-2" style={{ color: "var(--ink)" }}>
            {u.name || u.email}
            {u.is_self && (
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "var(--accent-soft)", color: "#8a6100" }}>YOU</span>
            )}
          </div>
          <div className="text-xs truncate" style={{ color: "var(--muted)" }}>
            {u.email} · {u.role === "baker" ? "Baker/Chef" : "Customer"} · {t.name}
            {u.auth_provider === "google" ? " · Google" : ""}
          </div>
        </div>
      </div>
      <button
        disabled={u.is_self}
        onClick={() => onDelete(u)}
        data-testid={`delete-account-${u.id}`}
        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-40"
        style={{ color: u.is_self ? "var(--muted)" : "#b91c1c", border: `1px solid ${u.is_self ? "var(--border)" : "#fca5a5"}` }}
        title={u.is_self ? "You can't delete your own account" : "Delete account"}
      >
        <Trash2 size={15} /> Delete
      </button>
    </div>
  );
};

export default function Accounts() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null); // { type: "one"|"all", target? }
  const [working, setWorking] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    api.listUsers().then(setUsers).catch((e) => toast.error(e.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const bakers = users.filter((u) => u.role === "baker");
  const customers = users.filter((u) => u.role === "customer");
  const deletableCount = users.filter((u) => !u.is_self).length;

  const runConfirm = async () => {
    if (!confirm) return;
    setWorking(true);
    try {
      if (confirm.type === "one") {
        await api.deleteUser(confirm.target.id);
        toast.success(`Deleted ${confirm.target.email}`);
      } else {
        const res = await api.deleteAllUsers();
        toast.success(`Deleted ${res.deleted} account${res.deleted === 1 ? "" : "s"}`);
      }
      setConfirm(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setWorking(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl" style={{ color: "var(--ink)" }}>Accounts</h1>
          <p className="text-sm mt-1" style={{ color: "var(--muted)" }}>
            All customer & baker/chef accounts across the app.
          </p>
        </div>
        {deletableCount > 0 && (
          <button
            onClick={() => setConfirm({ type: "all" })}
            data-testid="delete-all-accounts-btn"
            className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-sm font-semibold"
            style={{ color: "#b91c1c", border: "1px solid #fca5a5", background: "var(--surface)" }}
          >
            <ShieldAlert size={15} /> Delete all
          </button>
        )}
      </div>

      {loading ? (
        <div className="mt-6 space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl animate-pulse" style={{ background: "var(--surface-2)" }} />
          ))}
        </div>
      ) : users.length === 0 ? (
        <p className="mt-8 text-sm" style={{ color: "var(--muted)" }}>No accounts yet.</p>
      ) : (
        <div className="mt-6 space-y-6">
          <section>
            <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--muted)" }}>
              Bakers / Chefs ({bakers.length})
            </div>
            <div className="space-y-2">
              {bakers.length === 0
                ? <p className="text-sm" style={{ color: "var(--muted)" }}>None.</p>
                : bakers.map((u) => <AccountRow key={u.id} u={u} onDelete={(target) => setConfirm({ type: "one", target })} />)}
            </div>
          </section>
          <section>
            <div className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: "var(--muted)" }}>
              Customers ({customers.length})
            </div>
            <div className="space-y-2">
              {customers.length === 0
                ? <p className="text-sm" style={{ color: "var(--muted)" }}>None.</p>
                : customers.map((u) => <AccountRow key={u.id} u={u} onDelete={(target) => setConfirm({ type: "one", target })} />)}
            </div>
          </section>
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(v) => !v && !working && setConfirm(null)}>
        <AlertDialogContent data-testid="account-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.type === "all" ? "Delete all accounts?" : "Delete this account?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.type === "all"
                ? `This removes every customer and baker/chef account except your own (${user?.email}). Their orders stay but are unlinked. This cannot be undone.`
                : `${confirm?.target?.email} will be removed and their orders unlinked. This cannot be undone.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={working}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); runConfirm(); }}
              data-testid="account-confirm-delete-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {working ? "Deleting…" : confirm?.type === "all" ? "Delete all" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
